<template>
  <div class="container-fluid">
    <TopSection
      :title="title | capitalize"
      :section="section"
      :back-section="users-list"
      :sub-section="$t('create')"
    >
      <template v-slot:actions>
        <span
          class="btn btn-outline-primary px-4 p-2 mr-2"
          @click="goToUsersList"
        >
          <span>{{ $t("cancel") | capitalize }}</span>
        </span>

        <span
          v-if="isSuperUser"
          class="btn btn-primary px-4 p-2 ml-2"
          @click="createUser"
        >
          <spinner v-if="saving" />
          <span v-else>{{ $t("create") | capitalize }}</span>
        </span>
      </template>
    </TopSection>

    <div class="row pt-4">
      <div class="col-4">
        <!-- Name -->
        <div>
          <div class="table-main-text">
            {{ $t("name_and_last_name") | capitalize }}
          </div>
          <input
            v-model="name_and_last_name"
            minlength="3"
            maxlength="30"
            class="form-control"
          >
        </div>

        <!-- Email -->
        <div>
          <div class="table-main-text py-2">
            {{ $t("email") | capitalize }}
          </div>
          <input
            v-model="email"
            class="form-control"
            type="email"
          >
        </div>

        <!-- Organization -->
        <div v-if="!id">
          <div class="table-main-text py-2">
            {{ $t("organization") | capitalize }}
          </div>

          <Multiselect
            v-model="member"
            :placeholder="$t('select_member')"
            :options="members"
            track-by="id"
            label="username"
            :hide-selected="true"
            :show-labels="false"
          />

          <!-- User Type -->
          <div v-show="member!=='' && isMlcMember(member)">
            <div class="py-2">
              {{ $t("Role") | capitalize }}
            </div>
            <Multiselect
              v-model="user_type"
              :placeholder="$t('select_type')"
              :allow-empty="false"
              :options="users_types"
              :hide-selected="true"
              :show-labels="false"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "../../../assets/styles/libs";

.multiselect {
  min-width: auto !important;
}

input {
  height: 48px;
  padding: 6px;
  color: black;
  border: 1px solid #c0c5cf;
  border-radius: 4px;

  &:invalid {
    border: 2px solid red !important;
  }
}
</style>


<script>
import { isDefinedString, validateEmail } from "@/assets/libs/utils"
import { mapActions, mapGetters, mapState } from "vuex"
import { ApiCalls } from "@/assets/libs/services"
import { BASIC } from "@/store/modules/consts"

export default {
  name: "UsersCreate",
  data () {
    return {
      name_and_last_name: "",
      member: "",
      email: "",
      user_type: "",
      saving: false
    }
  },
  computed: {
    ...mapGetters("soundsys", ["isSuperUser", "isAdvanced", "orgTypes"]),
    ...mapState("soundsys", ["users_types"]),
    ...mapState("members", ["members"]),
    title () {
      if (this.id) {
        return `${this.$t("create_user_for")} ${this.member.username}`
      } else {
        return this.$t("create_user")
      }
    },
    section () {
      if (this.id) {
        return `${this.member.username} / ${this.$t("users")}`
      } else {
        return this.$t("users")
      }
    }
  },
  methods: {
    ...mapActions("members", ["getMembers"]),
    isMlcMember (member) {
      try {
        return member.org_type === this.orgTypes.MLC
      } catch (err) {
        return false
      }
    },
    validate () {
      if (!isDefinedString(this.name_and_last_name)) {
        this.$toasted.global.toast(this.$t("name_and_last_name_not_defined"))
        return false
      } else if (!isDefinedString(this.email)) {
        this.$toasted.global.toast(this.$t("email_not_defined"))
      } else if (!validateEmail(this.email)) {
        this.$toasted.global.toast(this.$t("email_format_not_valid"))
        return false
      } else if (!isDefinedString(this.member) || this.member === null) {
        this.$toasted.global.toast(this.$t("member_not_defined"))
        return false
      } else if (
        this.isMlcMember(this.member) &&
        !isDefinedString(this.user_type)
      ) {
        this.$toasted.global.toast(this.$t("user_type_not_defined"))
        return false
      } else {
        return true
      }
    },
    createUser () {
      if (!this.validate()) return
      this.saving = true

      const data = {
        name_and_last_name: this.name_and_last_name,
        email: this.email,
        user_type: this.isMlcMember(this.member) ? this.user_type : BASIC,
        status: this.status,
      }

      if (this.member !== null) {
        data["member"] = this.member.id
      }

      ApiCalls.save_user(data)
        .then(() => {
          this.saving = false
          this.$toasted.global.toast(this.$t("user_saved"))
          this.goToUsersList()
        })
        .catch(error => {
          this.saving = false
          this.$toasted.global.toast(error.error)
        })
    },
    goToUsersList () {
      const member_id = this.id
      if(!member_id) {
        this.$router.push({ "name": "users-list" })
      } else {
        this.$router.push({ name: "members-users-list", params: { member_id } })
      }
    },
  },
  mounted () {
    // If we have an id, this means we're creating a user for an specific
    // member. Else, the list of all organizations is used to populate the organization select.
    this.getMembers()
      .then(() => {
        if (this.id) {
          this.member = this.members.filter(member => member.id === Number(this.id))[0]
        }
      })
  },
  props: {
    id: [Number, String]
  },
}
</script>

